import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bell,
  ChartNoAxesCombined,
  ChevronLeft,
  ChevronRight,
  CirclePlus,
  LayoutGrid,
  Handshake,
  Layers,
  LogOut,
  Megaphone,
  Settings as SettingsIcon,
  History,
  UserMinus,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLeads } from '../../context/LeadsContext';
import { KanbanBoard } from '../../components/KanbanBoard/KanbanBoard';
import { Logo } from '../../components/Logo/Logo';
import { AppNotification, isPaymentRequiredError, isPlanLimitError, getErrorMessage } from '../../types';
import { apiService } from '../../services/api';
import { wsService, WebSocketMessage } from '../../services/websocket';

import { StatsDashboard } from '../StatsDashboard/StatsDashboard';
import { Settings, TabType as SettingsTab } from '../Settings/Settings';
import { ContatosRh } from '../ContatosRh/ContatosRh';
import { CampaignManager } from '../CampaignManager/CampaignManager';
import { HistoricoDisparos } from '../HistoricoDisparos/HistoricoDisparos';
import { GestaoDescadastro } from '../GestaoDescadastro/GestaoDescadastro';
import { FunilIaRouter } from '../../features/funil-ia';
import './Dashboard.css';

const CAMPAIGNS_ENABLED = process.env.REACT_APP_CAMPAIGNS !== 'false';

export const Dashboard: React.FC = () => {
  const { usuario, logout, isAdmin, isGerente, isOwner } = useAuth();
  const { createLead } = useLeads();
  const [activeModule, setActiveModule] = useState<
    | 'crm'
    | 'contatos_rh'
    | 'stats'
    | 'settings'
    | 'campanhas'
    | 'parceiros'
    | 'historico_disparos'
    | 'gestao_descadastro'
    | 'funil_ia'
  >('crm');
  const [campanhasTab, setCampanhasTab] = useState<'lista' | 'execucao'>('lista');
  const prevActiveModuleRef = useRef<
    | 'crm'
    | 'contatos_rh'
    | 'stats'
    | 'settings'
    | 'campanhas'
    | 'parceiros'
    | 'historico_disparos'
    | 'gestao_descadastro'
    | 'funil_ia'
    | null
  >(null);

  useEffect(() => {
    const prev = prevActiveModuleRef.current;
    prevActiveModuleRef.current = activeModule;
    if (prev != null && prev !== 'campanhas' && activeModule === 'campanhas') {
      setCampanhasTab('lista');
    }
  }, [activeModule]);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showInlineCreateLead, setShowInlineCreateLead] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifPanelRef = useRef<HTMLDivElement>(null);
  const refreshCampaignsRef = useRef<(() => void) | null>(null);

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [pendingLeadId, setPendingLeadId] = useState<string | null>(null);
  const [settingsInitialTab, setSettingsInitialTab] = useState<SettingsTab | undefined>(undefined);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Form state
  const [newLead, setNewLead] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    email: '',
    produtoInteresse: '',
    contatoRh: false,
    nomeEmpresa: '',
    observacao: '',
  });

  const handleCreateLead = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newLead.nome.trim() || !newLead.telefone.trim()) {
      setCreateError('Nome e telefone são obrigatórios');
      return;
    }

    if (newLead.contatoRh && !newLead.nomeEmpresa.trim()) {
      setCreateError('Nome da empresa é obrigatório quando Contato RH está marcado');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      await createLead({
        nome: newLead.nome.trim(),
        telefone: newLead.telefone.replace(/\D/g, ''),
        cpf: newLead.cpf.replace(/\D/g, '') || undefined,
        email: newLead.email.trim() || undefined,
        produtoInteresse: newLead.produtoInteresse.trim() || undefined,
        contatoRh: newLead.contatoRh,
        nomeEmpresa: newLead.contatoRh ? (newLead.nomeEmpresa.trim() || undefined) : undefined,
        observacao: newLead.observacao.trim() || undefined,
      });

      // Reset form and close modal
      setNewLead({
        nome: '',
        telefone: '',
        cpf: '',
        email: '',
        produtoInteresse: '',
        contatoRh: false,
        nomeEmpresa: '',
        observacao: '',
      });
      setShowInlineCreateLead(false);
      setActiveModule('crm');
    } catch (err: any) {
      if (isPaymentRequiredError(err)) {
        setCreateError(getErrorMessage(err) + ' Faça upgrade do plano.');
      } else if (isPlanLimitError(err)) {
        setCreateError(getErrorMessage(err));
      } else {
        setCreateError(getErrorMessage(err));
      }
    } finally {
      setCreating(false);
    }
  };

  const resetLeadForm = () => {
    setCreateError(null);
    setNewLead({
      nome: '',
      telefone: '',
      cpf: '',
      email: '',
      produtoInteresse: '',
      contatoRh: false,
      nomeEmpresa: '',
      observacao: '',
    });
  };

  const toggleInlineCreateLead = () => {
    if (!showInlineCreateLead) {
      resetLeadForm();
    }
    setShowInlineCreateLead((prev) => !prev);
  };

  const getRoleLabel = (role: string | undefined) => {
    switch (role) {
      case 'owner': return 'Proprietário';
      case 'admin': return 'Administrador';
      case 'gerente': return 'Gerente';
      case 'atendente': return 'Atendente';
      default: return role || 'Atendente';
    }
  };

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  // Load notifications on mount
  useEffect(() => {
    apiService.listNotifications().then(setNotifications).catch(console.error);
  }, []);

  // Listen for real-time lead_assigned notifications
  const handleLeadAssigned = useCallback((msg: WebSocketMessage) => {
    const notif = msg.data as AppNotification;
    if (notif && notif.targetUsuarioId === usuario?.usuarioId) {
      setNotifications((prev) => [notif, ...prev]);
    }
  }, [usuario?.usuarioId]);

  useEffect(() => {
    wsService.on('lead_assigned', handleLeadAssigned);
    return () => { wsService.off('lead_assigned', handleLeadAssigned); };
  }, [handleLeadAssigned]);

  // Close notification panel on outside click
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node)) {
        setShowNotifPanel(false);
      }
    };
    if (showNotifPanel) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
  }, [showNotifPanel]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = async (notif: AppNotification) => {
    if (!notif.read) {
      try {
        await apiService.markNotificationAsRead(notif.notificationId);
        setNotifications((prev) =>
          prev.map((n) => (n.notificationId === notif.notificationId ? { ...n, read: true } : n))
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    setShowNotifPanel(false);
    if (notif.type === 'ia_nao_configurada') {
      setSettingsInitialTab('ai');
      setActiveModule('settings');
      return;
    }
    if (notif.leadId) {
      setPendingLeadId(notif.leadId);
      setActiveModule('crm');
    }
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await apiService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.notificationId !== notificationId));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const formatTimeAgo = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'agora';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div className="dashboard">
      <div className={`dashboard-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-top">
            <button
              type="button"
              className="icon-button"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
              title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {sidebarCollapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>

          <div className="sidebar-nav">
            {!sidebarCollapsed && <div className="sidebar-group-label">CRM</div>}
            <button
              type="button"
              className={`sidebar-item ${activeModule === 'crm' && showInlineCreateLead ? 'active' : ''}`}
              onClick={() => {
                if (!showInlineCreateLead) {
                  resetLeadForm();
                }
                setActiveModule('crm');
                setShowInlineCreateLead(true);
              }}
              title="Criar Lead"
            >
              <span className="menu-dot" aria-hidden="true">
                <CirclePlus size={15} />
              </span>
              {!sidebarCollapsed && <span>Criar Lead</span>}
            </button>
            <button
              type="button"
              className={`sidebar-item ${activeModule === 'crm' && !showInlineCreateLead ? 'active' : ''}`}
              onClick={() => {
                setActiveModule('crm');
                setShowInlineCreateLead(false);
              }}
              title="Kanban de leads"
            >
              <span className="menu-dot" aria-hidden="true">
                <LayoutGrid size={15} />
              </span>
              {!sidebarCollapsed && <span>Leads</span>}
            </button>
            {(isAdmin || isGerente) && (
              <button
                type="button"
                className={`sidebar-item ${activeModule === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveModule('stats')}
                title="Dashboard"
              >
                <span className="menu-dot" aria-hidden="true">
                  <ChartNoAxesCombined size={15} />
                </span>
                {!sidebarCollapsed && <span>Dashboard</span>}
              </button>
            )}
            {CAMPAIGNS_ENABLED && (
              <button
                className={`sidebar-item${activeModule === 'campanhas' ? ' active' : ''}`}
                onClick={() => {
                  setActiveModule('campanhas');
                  setCampanhasTab('lista');
                }}
                title="Gerenciar Campanhas"
              >
                <span className="menu-dot" aria-hidden="true">
                  <Megaphone size={15} />
                </span>
                {!sidebarCollapsed && <span>Campanhas</span>}
              </button>
            )}
            {CAMPAIGNS_ENABLED && (
              <button
                type="button"
                className={`sidebar-item ${activeModule === 'funil_ia' ? 'active' : ''}`}
                onClick={() => setActiveModule('funil_ia')}
                title="Funil IA — campanhas ativas"
              >
                <span className="menu-dot" aria-hidden="true">
                  <Layers size={15} />
                </span>
                {!sidebarCollapsed && <span>Funil IA</span>}
              </button>
            )}
            <button
              type="button"
              className={`sidebar-item ${activeModule === 'contatos_rh' ? 'active' : ''}`}
              onClick={() => setActiveModule('contatos_rh')}
              title="Gerenciar Parceiros"
            >
              <span className="menu-dot" aria-hidden="true">
                <Handshake size={15} />
              </span>
              {!sidebarCollapsed && <span>Gerenciar Parceiros</span>}
            </button>
            <button
              type="button"
              className={`sidebar-item ${activeModule === 'historico_disparos' ? 'active' : ''}`}
              onClick={() => setActiveModule('historico_disparos')}
              title="Histórico de Disparos"
            >
              <span className="menu-dot" aria-hidden="true">
                <History size={15} />
              </span>
              {!sidebarCollapsed && <span>Histórico de Disparos</span>}
            </button>
            {(isAdmin || isGerente || isOwner) && (
              <button
                type="button"
                className={`sidebar-item ${activeModule === 'gestao_descadastro' ? 'active' : ''}`}
                onClick={() => setActiveModule('gestao_descadastro')}
                title="Gestão de descadastro"
              >
                <span className="menu-dot" aria-hidden="true">
                  <UserMinus size={15} />
                </span>
                {!sidebarCollapsed && <span>Gestão de descadastro</span>}
              </button>
            )}
          </div>

          {(isAdmin || isGerente) && (
            <div className="sidebar-bottom">
              <button
                type="button"
                className={`sidebar-item with-description ${activeModule === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveModule('settings')}
                title="Configurações"
              >
                <span className="menu-dot" aria-hidden="true">
                  <SettingsIcon size={14} />
                </span>
                {!sidebarCollapsed && (
                  <span className="menu-text-block">
                    <strong>Configurações</strong>
                    <small>Usuários, WhatsApp e IA</small>
                  </span>
                )}
              </button>
            </div>
          )}
        </aside>

        <div className="dashboard-main-area">
          <header className="dashboard-topbar">
            <div className="dashboard-topbar-left">
              <Logo size="medium" />
            </div>
            <div className="dashboard-topbar-right">
              <div className="notif-wrapper" ref={notifPanelRef}>
                <button
                  className="icon-button tiny notif-bell"
                  type="button"
                  title="Notificações"
                  aria-label="Notificações"
                  onClick={() => setShowNotifPanel((prev) => !prev)}
                >
                  <Bell size={14} />
                  {unreadCount > 0 && (
                    <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                </button>
                {showNotifPanel && (
                  <div className="notif-panel">
                    <div className="notif-panel-header">
                      <strong>Notificacoes</strong>
                      <span className="notif-panel-count">{unreadCount} novas</span>
                    </div>
                    <div className="notif-panel-list">
                      {notifications.length === 0 ? (
                        <p className="notif-empty">Nenhuma notificacao.</p>
                      ) : (
                        notifications.slice(0, 20).map((notif) => (
                          <button
                            key={notif.notificationId}
                            type="button"
                            className={`notif-item ${notif.read ? '' : 'unread'}`}
                            onClick={() => void handleNotificationClick(notif)}
                          >
                            <div className="notif-item-content">
                              <strong>{notif.title}</strong>
                              <span>{notif.message}</span>
                            </div>
                            <div className="notif-item-actions">
                              <span className="notif-item-time">{formatTimeAgo(notif.createdAt)}</span>
                              <button
                                type="button"
                                className="notif-delete-btn"
                                title="Excluir notificação"
                                onClick={(e) => void handleDeleteNotification(e, notif.notificationId)}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="topbar-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className="user-pill user-pill-button"
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                >
                  <span className="user-avatar">{(usuario?.nome?.[0] || 'U').toUpperCase()}</span>
                  <span>{usuario?.nome || 'Usuário Logado'}</span>
                </button>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <button
                      type="button"
                      className="user-dropdown-item"
                      onClick={logout}
                    >
                      <LogOut size={14} />
                      Sair ({getRoleLabel(usuario?.role)})
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="dashboard-main">
          {activeModule === 'crm' || activeModule === 'contatos_rh' ? (
            <>
              {activeModule === 'crm' ? (
                <KanbanBoard
                  onCreateLead={toggleInlineCreateLead}
                  isCreateLeadExpanded={showInlineCreateLead}
                  initialLeadId={pendingLeadId}
                  onInitialLeadConsumed={() => setPendingLeadId(null)}
                  refreshCampaignsRef={refreshCampaignsRef}
                  createLeadPanel={showInlineCreateLead ? (
                    <section className="inline-create-lead-panel">
                      <div className="inline-create-lead-header">
                        <h3>Novo Lead</h3>
                        <button
                          type="button"
                          className="inline-close-button"
                          onClick={() => setShowInlineCreateLead(false)}
                        >
                          Fechar
                        </button>
                      </div>
                      <form className="create-lead-form" onSubmit={handleCreateLead}>
                        {createError && (
                          <div className="form-error">{createError}</div>
                        )}
                        <div className="create-lead-grid">
                          <div className="form-group">
                            <label>Nome</label>
                            <input
                              type="text"
                              value={newLead.nome}
                              onChange={e => setNewLead({ ...newLead, nome: e.target.value })}
                              placeholder="Nome do lead"
                              required
                              disabled={creating}
                            />
                          </div>
                          <div className="form-group">
                            <label>Telefone</label>
                            <input
                              type="tel"
                              value={newLead.telefone}
                              onChange={e => setNewLead({ ...newLead, telefone: e.target.value })}
                              placeholder="+55 11 99999-9999"
                              required
                              disabled={creating}
                            />
                          </div>
                          <div className="form-group">
                            <label>CPF</label>
                            <input
                              type="text"
                              value={newLead.cpf}
                              onChange={e => setNewLead({ ...newLead, cpf: e.target.value })}
                              placeholder="000.000.000-00"
                              disabled={creating}
                            />
                          </div>
                          <div className="form-group">
                            <label>Email</label>
                            <input
                              type="email"
                              value={newLead.email}
                              onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                              placeholder="email@exemplo.com"
                              disabled={creating}
                            />
                          </div>
                          <div className="form-group form-group-full">
                            <label>Produto de Interesse</label>
                            <input
                              type="text"
                              value={newLead.produtoInteresse}
                              onChange={e => setNewLead({ ...newLead, produtoInteresse: e.target.value })}
                              placeholder="Qual produto ou serviço?"
                              disabled={creating}
                            />
                          </div>
                          <div className="form-group">
                            <label>Contato RH</label>
                            <select
                              value={newLead.contatoRh ? 'sim' : 'nao'}
                              onChange={e => {
                                const contatoRh = e.target.value === 'sim';
                                setNewLead({
                                  ...newLead,
                                  contatoRh,
                                  nomeEmpresa: contatoRh ? newLead.nomeEmpresa : '',
                                });
                              }}
                              disabled={creating}
                            >
                              <option value="nao">Não</option>
                              <option value="sim">Sim</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Nome da Empresa</label>
                            <input
                              type="text"
                              value={newLead.nomeEmpresa}
                              onChange={e => setNewLead({ ...newLead, nomeEmpresa: e.target.value })}
                              placeholder="Empresa do contato RH"
                              disabled={creating || !newLead.contatoRh}
                            />
                          </div>
                          <div className="form-group form-group-full">
                            <label>Observação</label>
                            <textarea
                              value={newLead.observacao}
                              onChange={e => setNewLead({ ...newLead, observacao: e.target.value })}
                              placeholder="Informações adicionais..."
                              rows={4}
                              disabled={creating}
                            />
                          </div>
                        </div>
                        <div className="create-lead-actions">
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setShowInlineCreateLead(false)}
                            disabled={creating}
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={creating}
                          >
                            {creating ? 'Criando...' : 'Criar Lead'}
                          </button>
                        </div>
                      </form>
                    </section>
                  ) : null}
                />
              ) : (
                <ContatosRh />
              )}
            </>
          ) : activeModule === 'funil_ia' && CAMPAIGNS_ENABLED ? (
            <div className="embedded-module embedded-module--funil-ia">
              <FunilIaRouter
                onOpenLead={(leadId) => {
                  setPendingLeadId(leadId);
                  setActiveModule('crm');
                }}
              />
            </div>
          ) : activeModule === 'campanhas' && CAMPAIGNS_ENABLED ? (
            <div className="embedded-module campaigns-hub">
              <div className="campaigns-hub-tabs" role="tablist" aria-label="Campanhas">
                <button
                  type="button"
                  role="tab"
                  aria-selected={campanhasTab === 'lista'}
                  className={`campaigns-hub-tab ${campanhasTab === 'lista' ? 'active' : ''}`}
                  onClick={() => setCampanhasTab('lista')}
                >
                  Campanhas
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={campanhasTab === 'execucao'}
                  className={`campaigns-hub-tab ${campanhasTab === 'execucao' ? 'active' : ''}`}
                  onClick={() => setCampanhasTab('execucao')}
                >
                  Execução
                </button>
              </div>
              {campanhasTab === 'lista' ? (
                <CampaignManager onOpenExecution={() => setCampanhasTab('execucao')} />
              ) : (
                <KanbanBoard
                  variant="campaignsExecution"
                  refreshCampaignsRef={refreshCampaignsRef}
                />
              )}
            </div>
          ) : activeModule === 'historico_disparos' ? (
            <div className="embedded-module">
              <HistoricoDisparos />
            </div>
          ) : activeModule === 'gestao_descadastro' ? (
            <div className="embedded-module">
              <GestaoDescadastro />
            </div>
          ) : activeModule === 'stats' ? (
            <div className="embedded-module">
              <StatsDashboard
                embedded
                onBackToLeads={() => setActiveModule('crm')}
                onOpenSettings={() => setActiveModule('settings')}
              />
            </div>
          ) : activeModule === 'settings' ? (
            <div className="embedded-module">
              <Settings
                embedded
                onBackToDashboard={() => {
                  setSettingsInitialTab(undefined);
                  setActiveModule('crm');
                }}
                onOpenStatsDashboard={() => setActiveModule('stats')}
                initialTab={settingsInitialTab}
              />
            </div>
          ) : (
            <section className="placeholder-module">
              <div className="placeholder-header">
                <div>
                  <h2>
                    {activeModule === 'parceiros' && 'Gerenciar Parceiros'}
                  </h2>
                  <p>
                    {activeModule === 'parceiros' && 'Cadastro e acompanhamento de parceiros comerciais.'}
                  </p>
                </div>
                <button
                  type="button"
                  className="back-to-kanban"
                  onClick={() => setActiveModule('crm')}
                >
                  Voltar ao kanban
                </button>
              </div>
              <div className="placeholder-grid">
                <article className="placeholder-card">
                  <strong>Bloco principal</strong>
                  <span>Espaço para componentes reais dessa funcionalidade.</span>
                </article>
                <article className="placeholder-card">
                  <strong>Resumo</strong>
                  <span>Indicadores rápidos da tela selecionada.</span>
                </article>
                <article className="placeholder-card">
                  <strong>Atividade recente</strong>
                  <span>Lista ou timeline com ações do usuário.</span>
                </article>
              </div>
            </section>
          )}
          </main>
        </div>
      </div>
    </div>
  );
};
